import React, { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import '../App.css';
import UserAudienceSection from './userAudienceSection';
import TopSummaryRow from './topSummaryRow';
import UsersTableSection from './usersTableSection';

import PageLoader from '../pageLoader';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const UsersDashboard = (props) => {
  const { setLoading, loading } = props;

  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    id: '',
    firstName: '',
    lastName: '',
    createdAt: '',
    updatedAt: '',
    email: '',
    country: '',
    platform: '',
    status: '',
    subscriptionType: '',
    isLogged: '',
    isLicenseActive: '',
    profileCompleted: '',
    profession: '',
    ageGroup: '',
    specialty: '',
    institution: ''
  });
  const [byAgeGroup, setByAgeGroup] = useState([]);
  const [byCountry, setByCountry] = useState([]);
  const [byProfession, setByProfession] = useState([]);
  const [subscriptionLookupStatus, setSubscriptionLookupStatus] = useState(null);

  // Simple per-column width state (all start equal, user can resize)
  const [columnWidths, setColumnWidths] = useState({
    firstName: 140,
    lastName: 140,
    email: 220,
    country: 120,
    createdAt: 180,
    platform: 120,
    subscriptionType: 200,
    isLicenseActive: 140,
    profileCompleted: 160,
    profession: 160,
    ageGroup: 140,
    specialty: 160,
    institution: 200,
  });

  const handleSearchChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const formatDateTime = (createdAt) => createdAt ? format(new Date(createdAt), "yyyy-MM-dd HH:mm:ss") : "";

  const exportToExcel = (data, filename) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
    XLSX.writeFile(workbook, `${filename}.xlsx`);
  };

  const exportUsers = () => {
    const exportData = filteredUsers.map(u => ({
      ID: u.id || '',
      'First Name': u.firstName || '',
      'Last Name': u.lastName || '',
      Created: u.createdAt ? format(new Date(u.createdAt), 'yyyy-MM-dd HH:mm:ss') : '',
      Updated: u.updatedAt ? format(new Date(u.updatedAt), 'yyyy-MM-dd HH:mm:ss') : '',
      Email: u.email || '',
      Country: u.country || '',
      Platform: u.platform || '',
      Status: u.subscriptionStatus || '',
      'Subscription Type': u.subscriptionType || '',
      'Is Logged': u.isLogged ? 'Yes' : 'No',
      'License Active': u.isLicenseActive ? 'Yes' : 'No',
      'Profile Completed': u.profileCompleted ? 'Yes' : 'No',
      Profession: u.profession || '',
      'Age Group': u.ageGroup || '',
      Specialty: u.specialty || '',
      Institution: u.institution || '',
    }));
    exportToExcel(exportData, `users_${format(new Date(), 'yyyy-MM-dd')}`);
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      // Prefer explicit endpoint if set, otherwise fall back to provided users URL
      let baseUrl;
      if (process.env.REACT_APP_API_USERS_ENDPOINT) {
        baseUrl = process.env.REACT_APP_API_BASE_URL || '';
        baseUrl = `${baseUrl}${process.env.REACT_APP_API_USERS_ENDPOINT}`;
      } else {
        baseUrl = 'https://anesthesiaassistant-be.onrender.com/api/v1/users/';
      }
      
      // Fetch all users using pagination
      const allUsers = [];
      const limit = 100;
      let skip = 0;
      let hasMore = true;
      
      while (hasMore) {
        // Handle URL construction - check if baseUrl already has query parameters
        const separator = baseUrl.includes('?') ? '&' : '?';
        const url = `${baseUrl}${separator}skip=${skip}&limit=${limit}`;
        const res = await fetch(url);
        const data = await res.json();
        const list = Array.isArray(data?.users) ? data.users : (Array.isArray(data) ? data : []);
        
        if (list.length === 0) {
          hasMore = false;
        } else {
          allUsers.push(...list);
          // If we got fewer results than the limit, we've reached the end
          if (list.length < limit) {
            hasMore = false;
          } else {
            skip += limit;
          }
        }
      }
      
      // Anesthesia Assistant appId as fallback
      const ANESTHESIA_ASSISTANT_APP_ID = '674997d8c4aaadc4054b3372';
      
      const normalized = allUsers.map((u) => ({
        id: u.id,
        iaphub_id: u.iaphub_user_identity,
        appId: u.app_id || u.appId || ANESTHESIA_ASSISTANT_APP_ID,
        iaphub_user_identity: u.iaphub_user_identity || '',
        firstName: u.first_name || '',
        lastName: u.last_name || '',
        createdAt: u.created_at || u.createdAt || null,
        updatedAt: u.updated_at || null,
        email: u.email || '',
        country: u.country || '',
        platform: u.provider || u.platform || '',
        isLogged: u.is_logged === true,
        isLicenseActive: !!u.is_license_active,
        profileCompleted: !!u.profile_completed,
        profession: u.profession || '',
        ageGroup: u.age_group || '',
        subscriptionStatus: (u.is_license_active === true || (typeof u.subscriptionStatus === 'string' && u.subscriptionStatus)) 
          ? (u.is_license_active ? 'Active' : (u.subscriptionStatus || 'Inactive')) 
          : '',
        subscriptionType: '',
        activeProducts: [],
        lastActiveAt: u.is_logged_at || u.updated_at || u.lastActiveAt || null,
        specialty: u.specialty || '',
        institution: u.institution || '',
      }));
      setUsers(normalized);
      
      await fetchUserSubscriptions(normalized);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      if (process.env.REACT_APP_ENABLE_ERROR_ALERTS === 'true') {
        alert('Error fetching users from server');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUserSubscriptions = async (userList) => {
    if (!Array.isArray(userList) || userList.length === 0) {
      return;
    }

    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    const endpoint = '/user/subscriptions/cached';
    
    if (!baseUrl) {
      console.warn('Subscription lookup skipped: API base URL not configured.');
      return;
    }

    setSubscriptionLookupStatus({ completed: 0, total: userList.length });

    try {
      const response = await fetch(`${baseUrl}${endpoint}`);
      if (!response.ok) {
        console.error(`Failed to fetch cached subscriptions: ${response.status} ${response.statusText}`);
        setSubscriptionLookupStatus(null);
        return;
      }
      
      const data = await response.json();
      const cachedSubscriptions = data.subscriptions || {};
      
      // Merge cached subscription data with users
      setUsers((prev) =>
        prev.map((user) => {
          const cacheKey = user.id?.toString();
          const cached = cachedSubscriptions[cacheKey];
          console.log(cached);
          if (cached) {
            return {
              ...user,
              subscriptionType: cached.activeProducts.length > 0 && cached.activeProducts[0].sku ? 
              cached.activeProducts[0].sku == 'au_yearly_premium' ? 'Yearly' : 'Monthly' : '',
              activeProducts: Array.isArray(cached.activeProducts) ? cached.activeProducts : []
            };
          }
          
          return user;
        })
      );
      
      setSubscriptionLookupStatus({ completed: userList.length, total: userList.length });
      
      // Clear status after a brief delay
      setTimeout(() => {
        setSubscriptionLookupStatus(null);
      }, 500);
    } catch (error) {
      console.error('Error fetching cached subscriptions:', error);
      setSubscriptionLookupStatus(null);
    }
  };

  useEffect(() => {
      fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Aggregate metrics and chart datasets when users change
  useEffect(() => {
    if (!users || users.length === 0) {
      setByAgeGroup([]);
      setByCountry([]);
      setByProfession([]);
      return;
    }
    // Signups over time by month
    const monthly = {};
    const monthlyActive = {};
    users.forEach(u => {
      if (!u.createdAt) return;
      const d = new Date(u.createdAt);
      if (isNaN(d.getTime())) return;
      const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
      monthly[key] = (monthly[key] || 0) + 1;
      if (u.isLicenseActive) {
        monthlyActive[key] = (monthlyActive[key] || 0) + 1;
      }
    });
    const allMonths = Array.from(new Set([...Object.keys(monthly), ...Object.keys(monthlyActive)])).sort();
    // By age group
    const AGE_GROUP_ORDER = [
      "Under 25",
      "25–34",
      "35–44",
      "45–54",
      "55–64",
      "65+",
      "Prefer not to say",
    ];
    const ageOrderIndex = new Map(AGE_GROUP_ORDER.map((g, i) => [g, i]));
    const ageGroupCounts = {};
    users.forEach(u => {
      const ag = u.ageGroup;
      if (ag && ag.trim() !== '') {
        ageGroupCounts[ag] = (ageGroupCounts[ag] || 0) + 1;
      }
    });
    const allAgeGroups = Object.entries(ageGroupCounts).map(([name, value]) => ({ name, value })).filter(entry => entry.name !== 'Unknown');
    const totalAgeGroups = allAgeGroups.reduce((sum, item) => sum + item.value, 0);
    const sortedAgeGroups = allAgeGroups
      .sort((a, b) => {
        const ai = ageOrderIndex.has(a.name) ? ageOrderIndex.get(a.name) : 999;
        const bi = ageOrderIndex.has(b.name) ? ageOrderIndex.get(b.name) : 999;
        return ai - bi;
      })
      .map((item) => ({
        ...item,
        percent: totalAgeGroups > 0 ? (item.value / totalAgeGroups) * 100 : 0,
      }));
    setByAgeGroup(sortedAgeGroups);
    // By country“
    const countryCounts = {};
    users.forEach(u => {
      const c = u.country;
      if (c && c.trim() !== '') {
        countryCounts[c] = (countryCounts[c] || 0) + 1;
      }
    });
    const allCountries = Object.entries(countryCounts).map(([name, value]) => ({ name, value })).filter(entry => entry.name !== 'Unknown');
    const totalCountries = allCountries.reduce((sum, item) => sum + item.value, 0);
    setByCountry(allCountries.sort((a,b)=>b.value-a.value).slice(0,5).map(item => ({ ...item, percent: totalCountries > 0 ? (item.value / totalCountries) * 100 : 0 })));
    // By profession
    const professionCounts = {};
    users.forEach(u => {
      const p = u.profession;
      if (p && p.trim() !== '') {
        professionCounts[p] = (professionCounts[p] || 0) + 1;
      }
    });
    const allProfessions = Object.entries(professionCounts).map(([name, value]) => ({ name, value })).filter(entry => entry.name !== 'Unknown');
    const totalProfessions = allProfessions.reduce((sum, item) => sum + item.value, 0);
    setByProfession(allProfessions.sort((a,b)=>b.value-a.value).slice(0,5).map(item => ({ ...item, percent: totalProfessions > 0 ? (item.value / totalProfessions) * 100 : 0 })));
  }, [users]);

  const filteredUsers = useMemo(() => {
    const filtered = users.filter(u => {
      const id = (u.id || '').toString().toLowerCase();
      const firstName = (u.firstName || '').toLowerCase();
      const lastName = (u.lastName || '').toLowerCase();
      const createdAt = (u.createdAt || '').toString().toLowerCase();
      const updatedAt = (u.updatedAt || '').toString().toLowerCase();
      const email = (u.email || '').toLowerCase();
      const country = (u.country || '').toLowerCase();
      const platform = (u.platform || '').toLowerCase();
      const status = (u.subscriptionStatus || '').toLowerCase();
      const subscriptionType = (u.subscriptionType || '').toLowerCase();
      const profession = (u.profession || '').toLowerCase();
      const ageGroup = (u.ageGroup || '').toLowerCase();
      const specialty = (u.specialty || '').toLowerCase();
      const institution = (u.institution || '').toLowerCase();
      const isLoggedMatch = filters.isLogged === '' ? true : (filters.isLogged === 'yes' ? u.isLogged === true : u.isLogged === false);
      const isLicenseActiveMatch = filters.isLicenseActive === '' ? true : (filters.isLicenseActive === 'yes' ? u.isLicenseActive === true : u.isLicenseActive === false);
      const profileCompletedMatch = filters.profileCompleted === '' ? true : (filters.profileCompleted === 'yes' ? u.profileCompleted === true : u.profileCompleted === false);

      // Subscription type filter:
      // - ''           -> All users (no additional filtering)
      // - 'subscribed' -> users with any subscription (Monthly or Yearly)
      // - 'monthly'    -> users with Monthly subscription
      // - 'yearly'     -> users with Yearly subscription
      // - 'none'       -> Non-subscribers (no active subscription type)
      let subscriptionTypeMatch = true;
      if (filters.subscriptionType === 'subscribed') {
        subscriptionTypeMatch = subscriptionType !== '';
      } else if (filters.subscriptionType === 'monthly') {
        subscriptionTypeMatch = subscriptionType === 'monthly';
      } else if (filters.subscriptionType === 'yearly') {
        subscriptionTypeMatch = subscriptionType === 'yearly';
      } else if (filters.subscriptionType === 'none') {
        subscriptionTypeMatch = subscriptionType === '';
      }

      return (
        id.includes(filters.id.toLowerCase()) &&
        firstName.includes(filters.firstName.toLowerCase()) &&
        lastName.includes(filters.lastName.toLowerCase()) &&
        createdAt.includes(filters.createdAt.toLowerCase()) &&
        updatedAt.includes(filters.updatedAt.toLowerCase()) &&
        email.includes(filters.email.toLowerCase()) &&
        country.includes(filters.country.toLowerCase()) &&
        platform.includes(filters.platform.toLowerCase()) &&
        status.includes(filters.status.toLowerCase()) &&
        subscriptionTypeMatch &&
        isLoggedMatch &&
        isLicenseActiveMatch &&
        profileCompletedMatch &&
        profession.includes(filters.profession.toLowerCase()) &&
        ageGroup.includes(filters.ageGroup.toLowerCase()) &&
        specialty.includes(filters.specialty.toLowerCase()) &&
        institution.includes(filters.institution.toLowerCase())
      );
    });
    
    // Sort by creation date (newest first)
    return filtered.sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return dateB - dateA; // Descending order (newest first)
    });
  }, [users, filters]);

  const paginated = filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const totalUsers = users.length;
  const newUsers90 = users.filter(u => {
    if (!u.createdAt) return false;
    const created = new Date(u.createdAt);
    if (isNaN(created.getTime())) return false;
    return Date.now() - created.getTime() <= 90 * 24 * 60 * 60 * 1000;
  }).length;
  const profileCompletedCount = users.filter(u => u.profileCompleted).length;
  const profileCompletedRate = totalUsers > 0 ? Math.round((profileCompletedCount / totalUsers) * 100) : 0;
  const deactivatedAccounts = users.filter(u => !u.isLicenseActive && !u.isLogged).length;
  
  // Calculate subscribed users (with active subscriptions)
  const subscribedUsers = users.filter(u => u.subscriptionType && u.subscriptionType !== null && u.subscriptionType !== '').length;
  const subscriptionRate = totalUsers > 0 ? Math.round((subscribedUsers / totalUsers) * 100) : 0;

  const uniqueProfessions = Array.from(new Set(users.map(u => u.profession).filter(Boolean)));
  const uniqueAgeGroups = Array.from(new Set(users.map(u => u.ageGroup).filter(Boolean)));

  return (
    <>
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      {loading ? (
        <PageLoader />
      ) : (
        <Box>
          <TopSummaryRow
            totalUsers={totalUsers}
            subscriptionRate={subscriptionRate}
            subscribedUsers={subscribedUsers}
            profileCompletedRate={profileCompletedRate}
            profileCompletedCount={profileCompletedCount}
            deactivatedAccounts={deactivatedAccounts}
          />              
          <UserAudienceSection
            ageGroups={byAgeGroup} 
            topCountries={byCountry} 
            professions={byProfession} 
          />
          <UsersTableSection
            exportUsers={exportUsers}
            subscriptionLookupStatus={subscriptionLookupStatus}
            filters={filters}
            handleSearchChange={handleSearchChange}
            uniqueProfessions={uniqueProfessions}
            uniqueAgeGroups={uniqueAgeGroups}
            columnWidths={columnWidths}
            paginated={paginated}
            formatDateTime={formatDateTime}
            filteredUsersLength={filteredUsers.length}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            PAGE_SIZE_OPTIONS={PAGE_SIZE_OPTIONS}
          />
        </Box>
      )}
    </Box>
    </>
  );
};

export default UsersDashboard;
